import React, { useEffect, useState, useContext } from 'react';
import CustomContent from '../../../components/Content/CustomContent';
import { Button, Card, Col, Divider, Form, Input, Row, Select, Typography, Space } from 'antd';
import Section from './Section';
import { v4 as uuidv4 } from 'uuid';
import { saveData, fetchData } from '../../service/dataServices';
import TranslatableInput from '../../components/form/TranslatableInput';
import { useTranslation } from 'react-i18next';
import UserContext from '../../../pages/layout/UserContext';
import checkPermission from '../../../components/Authorization/Util';
import ForbiddenResult from '../../../components/Authorization/ForbiddenResult';
import FormPreviewModal from './FormPreviewModal';

const { Title, Text } = Typography;

export default function NewForm() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const currentUser = useContext(UserContext);

  const hasWritePerm = checkPermission(currentUser, 'formSchema', 'write');
  const [isStarted, setIsStarted] = useState(false);
  const [sectionName, setSectionName] = useState({ en: "", ar: "" });
  const [activeSections, setActiveSections] = useState([]);
  const [sectionDataMap, setSectionDataMap] = useState({});
  //const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newFormId, setNewFormId] = useState();
  const [previewOpen, setPreviewOpen] = useState(false);

  //fetchData('/api/dropdowns/categories', response => setCategories(response));
  fetchData('/api/dropdowns', response => setGroups(response.group || []));


  useEffect(() => {
    if (isStarted) {
      setNewFormId(uuidv4());
    }
  }, [isStarted]);

  const onAddSection = () => {
    const hasContent = sectionName.en?.trim() || sectionName.ar?.trim();
    if (!hasContent) return;
    const newId = uuidv4();
    setActiveSections(prev => [...prev, { id: newId, name: sectionName }]);
    setSectionName({ en: "", ar: "" });
  };

  const deleteSection = (id) => {
    setActiveSections(prev => prev.filter(s => s.id !== id));
    setSectionDataMap(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const saveSectionData = (id, data) => {
    setSectionDataMap(prev => ({ ...prev, [id]: data }));
  };

  const PATH = "/api/form-schema"
  const saveMutation = saveData(PATH, "POST");

  const onStart = () => {
    form.validateFields().then(() => {
      setIsStarted(true);
    });
  };

  function onSubmit() {
    const formValues = form.getFieldsValue();
    const sections = Object.values(sectionDataMap);
    const dataToSend = {
      id: newFormId,
      name: formValues.formName,
      formIdPrefix: formValues.formIdPrefix,
      groupId: formValues.group,
      sections: sections,
    };
    console.log('datatosend', dataToSend)
    saveMutation.mutate({ dataToSend }, {
      onSuccess: () => {
        setIsStarted(false);
        form.resetFields();
        setActiveSections([]);
        setSectionDataMap({});
      }
    });
  }

  if (!hasWritePerm) {
    return <ForbiddenResult />;
  }

  return (
    <CustomContent>
      <Row justify="space-between" align="middle">
        <Col><Title level={4}>{t('forms.formTemplateDesigner')}</Title></Col>
        <Col>
          {Object.keys(sectionDataMap).length > 0 && (
            <Button onClick={() => setPreviewOpen(true)}>
              {t('customForm.preview', 'Preview')}
            </Button>
          )}
        </Col>
      </Row>

      <Row justify="center">
        <Col span={24}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
            >
              <Row gutter={16} align="bottom">
                <Col span={8}>
                  <Form.Item
                    name="formName"
                    label={t('forms.formName')}
                    rules={[{ required: true, message: t('forms.pleaseEnterFormName') }]}
                    initialValue={{ en: "", ar: "" }}
                  >
                    <TranslatableInput disabled={isStarted} placeholder={{ en: "e.g. Carpet Quality Check", ar: "مثال: فحص جودة السجاد" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="formIdPrefix"
                    label={t('forms.formIdPrefix')}
                    rules={[{ required: true, message: t('forms.pleaseEnterFormIdPrefix') }]}
                  >
                    <Input disabled={isStarted} placeholder="e.g. 'CT-'" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="group"
                    label={t('forms.group')}
                    rules={[{ required: true, message: t('forms.pleaseSelectGroup') }]}
                  >
                    <Select
                      disabled={isStarted}
                      placeholder={t('forms.selectGroup')}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {groups.map(group => (
                        <Select.Option key={group.id} value={group.id}>
                          {group.title}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {!isStarted && (
                <Row justify="center">
                  <Button type="primary" onClick={onStart}>{t('common.start')}</Button>
                </Row>
              )}
            </Form>
          </Card>
        </Col>
      </Row>

      {isStarted && (
        <Card>
          <Row justify="center" align="bottom" gutter={16}>
            <Col><Text strong>{t('forms.newSection')}</Text></Col>
            <Col span={8}>
              <TranslatableInput
                value={sectionName}
                onChange={setSectionName}
                placeholder="e.g. Material Analysis"
              />
            </Col>
            <Col><Button onClick={onAddSection} disabled={!sectionName.en?.trim() && !sectionName.ar?.trim()}>{t('forms.addSection')}</Button></Col>
          </Row>
        </Card>
      )}

      {activeSections.map((s) => (
        <Section key={s.id} id={s.id} name={s.name} deleteFn={deleteSection} saveFn={saveSectionData} />
      ))}

      {Object.keys(sectionDataMap).length > 0 && (
        <Row justify="center" style={{ padding: '60px 0' }}>
          <Space direction="vertical" align="center">
            <Button
              type="primary"
              size="large"
              onClick={onSubmit}
            >
              {t('forms.saveFormTemplate')}
            </Button>
          </Space>
        </Row>
      )}
      <FormPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        formName={form.getFieldValue('formName')}
        sections={Object.values(sectionDataMap)}
      />
    </CustomContent>
  );
}
